<template>
	<core-layout-content large>
		<core-layout-headline-and-actions>
			<template slot="headline">{{ $t("create_case") }}</template>
		</core-layout-headline-and-actions>

		<!-- Body -->
		<div class="flex wrap xs-12">
			<div class="xs-8 p0 md-pr10">
				<j-card class="xs-12 bg5 r2 p4">
					<!-- Select Product -->
					<div id="products" class="flex wrap xs-12">
						<label for="name" class="lh125 fw7 mb1">{{ $t("product") }}</label>
						<core-select-product
							name="products"
							:insuranceType="form.insuranceType"
							v-validate="'required'"
							:status="errors.first('products') ? 'error' : null"
							v-model="selectedProducts"
							@input="changeProduct"
						/>
						<span class="fs3 lh125 mt1 cError">{{
							errors.first("products")
						}}</span>
					</div>
					<div class="mt6">
						<label class="lh125 fw7 mb1">Payment Frequency</label>
						<div
							v-if="!productId || !annualOnly.includes(productId.plan)"
							class="flex xs-middle xs-left mt2"
						>
							<div>Annually</div>
							<j-base-toggle v-model="monthly" size="small" class="ml2 mr2" />
							<div>Monthly</div>
						</div>
						<div v-else>
							Annually Premium
						</div>
					</div>
				</j-card>
				<div>
					<div v-for="person in form.people" :key="person.index">
						<j-card
							v-if="person.isInsured || person.isHolder || person.isOtherHolder"
							class="xs-12 bg5 r2 mt8"
						>
							<div class="xs-12 p4 bc7 bb1 fs5 fw7 bg1 c5 card-header">
								<span v-if="person.index === 0">
									{{
										form.people[person.index].isHolder
											? $t("asia.proposed_insured_and_holder_details")
											: $t("asia.proposed_insured_details")
									}}
								</span>
								<span v-if="person.index === 1 && person.isHolder">
									{{ $t("asia.representative_holder_details") }}
								</span>
								<span v-if="person.isOtherHolder">
									{{ $t("asia.other_policy_holder_details") + " " }}
									{{ person.index - 1 }}
								</span>
							</div>
							<div class="p4">
								<core-form-input
									v-if="person.isHolder || person.isOtherHolder"
									componentName="j-base-select"
									:id="`salutation${person.index}`"
									:name="`salutation${person.index}`"
									:key="`salutation${person.index}`"
									:label="$t('asia.salutation')"
									:options="salutationOptions"
									v-validate="'required'"
									v-model="person.salutation"
									:validationErrors="errors"
									@blur="$validator.validate(`salutation${person.index}`)"
								/>

								<core-form-input
									componentName="j-base-input-text"
									:id="`nameFormat${person.index}`"
									:name="`nameFormat${person.index}`"
									:key="`nameFormat${person.index}`"
									:label="$t('asia.full_name')"
									:subheadline="$t('asia.full_name_note')"
									v-validate="'required'"
									v-model="person.nameFormat"
									class="mt6"
									:validationErrors="errors"
									@blur="$validator.validate(`nameFormat${person.index}`)"
								/>

								<core-form-input
									componentName="j-base-input-text"
									:id="`hkid${person.index}`"
									:name="`hkid${person.index}`"
									:key="`hkid${person.index}`"
									:label="$t('asia.hkid_number')"
									:subheadline="$t('asia.hkid_number_note')"
									v-validate="'required|hkid'"
									v-model="person.hkid"
									class="mt6"
									:validationErrors="errors"
									@blur="$validator.validate(`hkid${person.index}`)"
								/>

								<core-form-input
									componentName="j-input-date-of-birth"
									:id="`dateOfBirth${person.index}`"
									:name="`dateOfBirth${person.index}`"
									:key="`dateOfBirth${person.index}`"
									:label="$t('asia.dateOfBirth')"
									v-validate="validateAge"
									v-model="person.dateOfBirth"
									:validationErrors="errors"
									class="mt6"
									@blur="
										getPrice()
										$validator.validate(`dateOfBirth${person.index}`)
									"
								/>

								<core-form-input
									v-if="person.isInsured"
									componentName="j-base-radios"
									:id="`gender${person.index}`"
									:name="`gender${person.index}`"
									:key="`gender${person.index}`"
									:label="$t('asia.gender')"
									:options="genderOptions"
									v-validate="'required'"
									v-model="person.gender"
									:validationErrors="errors"
									class="mt6"
									@input="getPrice()"
								/>

								<div
									v-if="person.isInsured"
									class="flex wrap xs-12 xs-between mt6"
								>
									<div class="xs-12 md-6 pr0 md-pr2">
										<core-form-input
											componentName="j-base-input-number"
											:id="`heightInCm${person.index}`"
											:name="`heightInCm${person.index}`"
											:key="`heightInCm${person.index}`"
											:label="$t('asia.height')"
											:subheadline="$t('asia.height_note')"
											v-validate="'required'"
											v-model="person.heightInCm"
											:validationErrors="errors"
											@input="$validator.validate(`heightInCm${person.index}`)"
										/>
									</div>
									<div class="xs-12 md-6 pl0 md-pl2 mt6 md-mt0">
										<core-form-input
											componentName="j-base-input-number"
											:id="`weightInKg${person.index}`"
											:name="`weightInKg${person.index}`"
											:key="`weightInKg${person.index}`"
											:label="$t('asia.weight')"
											:subheadline="$t('asia.weight_note')"
											v-validate="'required'"
											v-model="person.weightInKg"
											:validationErrors="errors"
											@blur="$validator.validate(`weightInKg${person.index}`)"
										/>
									</div>
								</div>

								<core-form-input
									v-if="person.isInsured"
									componentName="j-base-select"
									:id="`occupation${person.index}`"
									:name="`occupation${person.index}`"
									:key="`occupation${person.index}`"
									:options="occupationOptions"
									:label="$t('asia.occupation')"
									v-validate="'required'"
									v-model="person.occupation"
									:validationErrors="errors"
									class="mt6"
								/>

								<div
									v-if="person.isInsured"
									class="flex wrap xs-12 xs-between mt6"
								>
									<div class="xs-12 md-5 pr0 md-pr2">
										<core-form-input
											componentName="j-base-radios"
											:id="`placeOfResidence${person.index}`"
											:name="`placeOfResidence${person.index}`"
											:key="`placeOfResidence${person.index}`"
											:label="$t('asia.place_of_residence')"
											:options="placeOfResidenceOptions"
											v-model="person.placeOfResidence"
										/>
									</div>
									<div class="xs-12 md-7 pr0">
										<core-form-input
											v-if="person.placeOfResidence !== 'hong_kong'"
											componentName="j-base-input-text"
											:id="`placeOfResidenceOther${person.index}`"
											:name="`placeOfResidenceOther${person.index}`"
											:key="`placeOfResidenceOther${person.index}`"
											:label="$t('asia.place_of_residence')"
											:subheadline="$t('asia.place_of_residence_note')"
											v-model="person.placeOfResidence"
											v-validate="'required'"
											:validationErrors="errors"
										/>
									</div>
								</div>

								<label
									v-if="person.isInsured"
									class="flex xs-12 xs-middle xs-between mt6"
								>
									<div class="xs-10 fw7 lh125 fs4">
										{{ $t("asia.insured_person_is_also") }}
									</div>
									<j-base-toggle
										v-model="person.isHolder"
										@input="toggleInsuredIsHolder(person.isHolder)"
										size="small"
									/>
								</label>

								<core-form-input
									v-if="person.isHolder || person.isOtherHolder"
									componentName="j-base-input-text"
									:id="`correspondenceAddress${person.index}`"
									:name="`correspondenceAddress${person.index}`"
									:key="`correspondenceAddress${person.index}`"
									:label="$t('asia.correspondence_address')"
									v-model="person.correspondenceAddress"
									class="mt6"
									v-validate="'required'"
									:validationErrors="errors"
									@blur="
										$validator.validate(`correspondenceAddress${person.index}`)
									"
								/>

								<core-form-input
									v-if="person.isHolder || person.isOtherHolder"
									componentName="j-base-input-text"
									:id="`email${person.index}`"
									:name="`email${person.index}`"
									:key="`email${person.index}`"
									:label="$t('asia.personal_email_address')"
									v-model="person.email"
									class="mt6"
									v-validate="'required|email'"
									:validationErrors="errors"
									@blur="$validator.validate(`email${person.index}`)"
								/>

								<core-form-input
									v-if="person.isHolder || person.isOtherHolder"
									componentName="j-base-input-text"
									:id="`telephone${person.index}`"
									:name="`telephone${person.index}`"
									:key="`telephone${person.index}`"
									:label="$t('asia.contact_telephone_no')"
									:subheadline="$t('asia.contact_telephone_no_note')"
									v-model="person.telephone"
									class="mt6"
									v-validate="'required|numeric|max:8'"
									:validationErrors="errors"
									@blur="$validator.validate(`telephone${person.index}`)"
								/>

								<core-form-input
									v-if="!person.isHolder && !person.isOtherHolder"
									componentName="j-base-select"
									:id="`relationshipsToHolder${person.index}`"
									:name="`relationshipsToHolder${person.index}`"
									:key="`relationshipsToHolder${person.index}`"
									:options="relationshipOptions"
									:label="$t('asia.relationship_to_holder')"
									v-model="person.relationshipsToHolder"
									class="mt6"
									v-validate="'required'"
									:validationErrors="errors"
									@blur="
										$validator.validate(`relationshipsToHolder${person.index}`)
									"
								/>
							</div>
							<div v-if="person.isOtherHolder" class="p4">
								<div class="flex xs-right">
									<j-button
										@click="removeHolder(person.index)"
										size="small"
										type="error"
									>
										{{ $t("asia.remove_this_policy_holder") }}
									</j-button>
								</div>
							</div>
						</j-card>
						<j-card v-if="person.isInsured" class="xs-12 bg5 r2 mt8">
							<div
								class="flex xs-12 xs-between xs-middle p4 bc7 bb1 fs5 fw7 bg1 c5 card-header"
							>
								{{ $t("asia.health_statement") }}
							</div>
							<div class="p4">
								<div class="c1 lh125 fw7 mb2">
									{{ $t("asia.illness_facts_text") }}
								</div>

								<!-- Toggle -->
								<label
									:for="f.type"
									class="flex xs-12 xs-middle xs-between bg4 bc7 b1 p2 mb1 cu2"
									v-for="f in form.illnessFacts"
									:key="f.type"
								>
									<div class="xs-10 lh125 fs3">{{ $t(`asia.${f.type}`) }}</div>
									<j-base-toggle size="small" :id="f.type" v-model="f.value" />
								</label>

								<!-- Other questions -->
								<label
									:for="f.type"
									class="flex xs-12 xs-middle xs-between mt6"
									v-for="f in form.otherFacts"
									:key="f.type"
								>
									<div class="xs-10 fw7 lh125 fs4">
										{{ $t(`asia.${f.type}`) }}
									</div>
									<j-base-toggle size="small" :id="f.type" v-model="f.value" />
								</label>
							</div>
						</j-card>
					</div>
					<div class="pt8 pb8 flex xs-right">
						<j-button @click="addOtherHolder">
							{{ $t("asia.add_another_policy_holder") }}
						</j-button>
					</div>
					<j-card class="xs-12 bg5 r2">
						<div
							class="flex xs-12 xs-between xs-middle p4 bc7 bb1 fs5 fw7 bg1 c5 card-header"
						>
							{{ $t("asia.policy_effective_date") }}
						</div>
						<div class="p4">
							<core-form-input
								componentName="j-base-date-picker-simple"
								name="policyEffectiveDate"
								:label="$t('asia.policy_effective_date_text')"
								v-model="form.policyEffectiveDate"
								:min-date="policyMinDate"
								:max-date="policyMaxDate"
								v-validate="'required'"
								:validationErrors="errors"
							/>
						</div>
					</j-card>
					<j-card class="xs-12 bg5 r2 mt8">
						<div
							class="flex xs-12 xs-between xs-middle p4 bc7 bb1 fs5 fw7 bg1 c5 card-header"
						>
							{{ $t("asia.claim_settlement_mode") }}
						</div>
						<div class="p4">
							<core-form-input
								componentName="j-base-select"
								name="settleMode"
								:label="$t('asia.settle_mode')"
								:description="$t('asia.settle_mode_note')"
								:options="settleModeOptions"
								v-model="form.settleMode"
								v-validate="'required'"
								:validationErrors="errors"
							/>
							<core-form-input
								v-if="form.settleMode === 'autopay'"
								componentName="j-base-input-text"
								:name="`bankName`"
								:label="$t('asia.bank_name')"
								v-model="form.bankName"
								class="mt6"
								v-validate="'required'"
								:validationErrors="errors"
							/>
							<div v-if="form.settleMode === 'autopay'" class="mt6">
								<label for="bankAccountNumber" class="xs-12 fw7 lh125 fs4">
									{{ $t("asia.bank_account_number") }}
								</label>
								<bank-number
									name="bankAccountNumber"
									:value="[]"
									v-model="form.bankAccountNumber"
									v-validate="'required'"
									:validationErrors="errors"
								/>
							</div>
							<core-form-input
								v-if="form.settleMode === 'autopay'"
								componentName="j-base-input-text"
								:name="`bankAccountFullName`"
								:label="$t('asia.full_name_off_bank_account_holder')"
								v-model="form.bankAccountFullName"
								class="mt6"
								v-validate="'required'"
								:validationErrors="errors"
							/>
							<div v-if="form.settleMode" class="mt6 lh125">
								{{ $t("asia.addition_services") }}
							</div>
						</div>
					</j-card>
					<!-- Extras -->
					<j-card class="xs-12 bg5 r2 mt8">
						<div
							class="flex xs-12 xs-between xs-middle p4 bc7 bb1 fs5 fw7 bg1 c5 card-header"
						>
							Extras
						</div>
						<div class="p4">
							<div>
								<core-form-input
									componentName="j-base-input-text"
									name="discountCode"
									label="Discount Code"
									v-model="form.discountCode"
									size="small"
								/>
								<!-- Error message -->
								<div
									class="xs-12 fs3 lh125 cError mt1"
									v-if="discountCodeError"
								>
									{{ discountCodeError }}
								</div>
								<div v-if="form.appliedDiscounts.length">
									<div
										v-for="d in form.appliedDiscounts"
										:key="d.code"
										class="flex xs-middle xs-between mt2 border b1 bc7 bg4 rounded pl3 pr3 pt2 pb2"
									>
										<div>{{ d.code }}</div>
										<div class="flex items-center">
											<div>
												{{ d.formattedFlat }}
											</div>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												viewBox="0 0 24 24"
												class="ml3 cu2"
												style="width: 1em; height: 1em"
												@click="removeDiscount()"
											>
												<path
													d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
													fill="currentColor"
												/>
											</svg>
										</div>
									</div>
								</div>
								<div class="flex">
									<j-button
										class="mt2"
										size="small"
										@click="applyDiscount(form.discountCode)"
									>
										{{ $t("asia.apply") }}
									</j-button>
								</div>
							</div>
							<core-form-input
								class="mt6"
								componentName="j-base-input-text"
								name="referralCode"
								label="Referral Code"
								description="If there is a referral involved, enter an internal code that can be found in Participants corner"
								v-model="form.referralCode"
								size="small"
							/>
							<!-- Error message -->
							<div class="xs-12 fs3 lh125 cError mt1" v-if="referralCodeError">
								{{ referralCodeError }}
							</div>
							<div v-if="form.appliedReferrals.length">
								<div
									v-for="d in form.appliedReferrals"
									:key="d.code"
									class="flex xs-middle xs-between mt2 border b1 bc7 bg4 rounded pl3 pr3 pt2 pb2"
								>
									<div>{{ d.name }}</div>
									<div class="flex items-center">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											class="ml3 cu2"
											style="width: 1em; height: 1em"
											@click="removeReferrals()"
										>
											<path
												d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
												fill="currentColor"
											/>
										</svg>
									</div>
								</div>
							</div>
							<div class="flex">
								<j-button
									class="mt2"
									size="small"
									@click="applyReferral(form.referralCode)"
								>
									{{ $t("asia.apply") }}
								</j-button>
							</div>
						</div>
					</j-card>
				</div>
				<j-button class="mt10 flex xs-middle" @click="onSubmit">
					<span>Submit</span>
					<j-spinner
						v-if="isLoading"
						:delay="0"
						type="light"
						size="small"
						class="ml2"
					/>
				</j-button>
			</div>
			<div class="xs-4 p0 md-pl4 mt6 md-mt0">
				<div class="asia-pricing-box-sticky">
					<j-card class="xs-12 p4 bg5">
						<j-spinner v-if="isPriceLoading" />
						<div
							class="xs-12 flex wrap mb5"
							v-for="(item, i) in priceBreakdown"
							:key="i"
						>
							<div class="xs-12 fs3 c6 fw7 mb1">{{ item.label }}</div>
							<div
								class="xs-12"
								:class="{ 'fw7 fs6': i === priceBreakdown.length - 1 }"
								:data-fup="`component/${item.id}`"
							>
								{{ item.value }}
							</div>
						</div>
						<div class="xs-12 flex xs-right">
							<j-button @click="getPrice" type="bordered" size="xsmall"
								>Refresh</j-button
							>
						</div>
					</j-card>
				</div>
			</div>
		</div>
	</core-layout-content>
</template>

<script>
import JCard from "@/components/JCard/"
import JButton from "@/components/JButton/"
import JSpinner from "@/components/JSpinner"
import JBaseToggle from "@/components/JBaseToggle"
// import JBaseInputText from "@/components/JBaseInputText"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent.vue"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions.vue"
import CoreSelectProduct from "@/modules/core/components/CoreSelectProduct.vue"
import CoreFormInput from "@/modules/core/components/CoreFormInput.vue"
import BankNumber from "./BankNumber.vue"
// import HtmlRenderer from "./HtmlRenderer.vue"
import dayjs from "dayjs"
import api from "@/api/gql.js"
import { transformDate } from "@/utils/"

export default {
	components: {
		JCard,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CoreSelectProduct,
		CoreFormInput,
		JSpinner,
		JButton,
		JBaseToggle,
		// JBaseInputText,
		BankNumber
		// HtmlRenderer
	},

	data() {
		return {
			isLoading: false,
			isPriceLoading: false,
			agreeTermAndConditions: false,
			agreeDeclare: false,
			agreeCommision: false,
			agreeCancellation: false,
			monthly: null,
			selectedProducts: [],
			annualOnly: ["S00027-01-000-02"],
			discountCodeError: "",
			referralCodeError: "",
			form: {
				products: [],
				appliedDiscounts: [],
				appliedReferrals: [],
				product: {
					name: "",
					pricing: {},
					productId: {}
				},
				agentBrokerCode: "",
				paymentFrequency: "",
				people: [],
				policyEffectiveDate: null,
				settleMode: null,
				bankName: "",
				// bankAccountNumber: [],
				bankAccountNumber: "",
				bankAccountFullName: "",
				illnessFacts: [
					{ type: "hasCancer", value: false },
					{
						type: "hasSkinDisorder",
						value: false
					},
					{
						type: "hasRespiratoryDisorder",
						value: false
					},
					{
						type: "hasDeviatedNasalSeptum",
						value: false
					},
					{
						type: "hasTuberculosis",
						value: false
					},
					{
						type: "hasThyroidDisorder",
						value: false
					},
					{ type: "hasHepatitis", value: false },
					{
						type: "hasLiverDisorder",
						value: false
					},
					{ type: "hasMalaria", value: false },
					{ type: "hasVaricoseVeins", value: false },
					{ type: "hasVertigo", value: false },
					{ type: "hasEpilepsy", value: false },
					{
						type: "hasCerebrovascularDisorder",
						value: false
					},
					{ type: "hasDiabetes", value: false },
					{
						type: "hasHypertension",
						value: false
					},
					{ type: "hasChestPain", value: false },
					{
						type: "hasCardiovascularDisorder",
						value: false
					},
					{
						type: "hasHeartDisorder",
						value: false
					},
					{ type: "hasUlcer", value: false },
					{
						type: "hasDigestiveEsophagusGastrointestinalTractDisorder",
						value: false
					},
					{
						type: "hasHaemorrhofieldIds",
						value: false
					},
					{ type: "hasHernia", value: false },
					{
						type: "hasKidneyDisorder",
						value: false
					},
					{ type: "hasHalluxValgus", value: false },
					{ type: "hasAutoimmuneDisorder", value: false },
					{
						type: "hasBloodDisorder",
						value: false
					},
					{
						type: "hasSpinalMuscularSkeletalDisorder",
						value: false
					},
					{ type: "hasArthritis", value: false },
					{ type: "hasBreastDisorder", value: false },
					{
						type: "hasGynecologicalConditions",
						value: false
					},
					{
						type: "hasUrinaryDisease",
						value: false
					},
					{
						type: "hasVenerealDisorder",
						value: false
					},
					{
						type: "hasHivAids",
						value: false
					},
					{
						type: "hasPregnancyComplications",
						value: false
					},
					{
						type: "hasPsychiatricProblems",
						value: false
					},
					{
						type: "hasOtherDisorders",
						value: false
					}
				],
				otherFacts: [
					{
						type: "hasHospitalTreatmentWithinFiveYears",
						value: false
					},
					{
						type: "hasUpcomingNeedForMedicalTreatment",
						value: false
					},
					{
						type: "hasCurrentNeedForMedication",
						value: false
					},
					{
						type: "isCurrentlyPregnant",
						value: false
					},
					{
						type: "hasLostWeightWithinSixMonths",
						value: false
					},
					{
						type: "hasCancerDiagnosis",
						value: false
					},
					{
						type: "hasFamilyBeenRejectedInsurance",
						value: false
					}
				]
			}
		}
	},
	computed: {
		productId() {
			return this.selectedProducts[0] && this.selectedProducts[0].productId
		},
		pricing() {
			return (this.form.products[0] && this.form.products[0].pricing) || {}
		},
		priceBreakdown() {
			// Combine taxes and discounts together and order based on order
			const appliedDiscounts = this?.pricing?.appliedDiscounts || []
			const appliedTaxes = this?.pricing?.appliedTaxes || []
			const taxesAndDiscounts = [...appliedDiscounts, ...appliedTaxes].sort(
				(a, b) => a.order - b.order
			)
			const getValue = item => {
				if (!item) return ""
				const ratio = item.ratio ? `${(item.ratio * 100).toFixed(3)}%` : null
				const flat = item.formattedFlat ? item.formattedFlat : null
				return `${flat} (${ratio} of ${item.formattedOriginalPrice})`
			}
			return [
				{
					label: "Gross Premium",
					value: this?.pricing?.formattedOriginalPrice || null
				},
				...taxesAndDiscounts.map(item => ({
					label: item.name ? item.name : item.code,
					value: getValue(item)
				})),
				{
					id: "netPremium",
					label: "Net Premium",
					value: this?.pricing?.formattedPrice
				}
			]
		},
		salutationOptions() {
			return [
				{
					value: "mr",
					name: this.$t("asia.mr")
				},
				{
					value: "ms",
					name: this.$t("asia.ms")
				},
				{
					value: "mrs",
					name: this.$t("asia.mrs")
				},
				{
					value: "miss",
					name: this.$t("asia.miss")
				}
			]
		},
		genderOptions() {
			return [
				{
					value: "male",
					name: this.$t("asia.male")
				},
				{
					value: "female",
					name: this.$t("asia.female")
				}
			]
		},
		occupationOptions() {
			return [
				{
					value: "001",
					name: this.$t("asia.occupation_001")
				},
				{
					value: "002",
					name: this.$t("asia.occupation_002")
				},
				{
					value: "003",
					name: this.$t("asia.occupation_003")
				},
				{
					value: "004",
					name: this.$t("asia.occupation_004")
				},
				{
					value: "005",
					name: this.$t("asia.occupation_005")
				}
			]
		},
		placeOfResidenceOptions() {
			return [
				{
					value: "hong_kong",
					name: this.$t("asia.hong_kong")
				},
				{
					value: null,
					name: this.$t("asia.other")
				}
			]
		},
		relationshipOptions() {
			return [
				{
					name: this.$t("asia.spouse"),
					value: "spouse"
				},
				{
					name: this.$t("asia.parent"),
					value: "parent"
				},
				{
					name: this.$t("asia.grandparent"),
					value: "grandparent"
				},
				{
					name: this.$t("asia.child"),
					value: "child"
				},
				{
					name: this.$t("asia.sibling"),
					value: "sibling"
				}
			]
		},
		settleModeOptions() {
			return [
				{
					name: this.$t("asia.autopay"),
					value: "autopay"
				},
				{
					name: this.$t("asia.cheque"),
					value: "cheque"
				}
			]
		},
		age() {
			if (this.form.people[0] && this.form.people[0].dateOfBirth) {
				return dayjs().diff(
					transformDate(this.form.people[0].dateOfBirth),
					"years"
				)
			} else {
				return null
			}
		},
		gender() {
			return (this.form.people[0] && this.form.people[0].gender) || "female"
		},
		/** Holder age can only be 18 - 100 years old
		 *  or if holder is also the insured 18 - 80 years old
		 */
		validateAge() {
			let max, min
			// 18 year olds - present date subtract 18 years
			max = dayjs()
				.add(-18, "year")
				.format("YYYY-MM-DD")
			// 100 year olds - present date subtract 100 years
			min = dayjs()
				.add(-80, "year")
				.format("YYYY-MM-DD")
			return `required|date_format:YYYY-MM-DD|date_between:${min},${max},inclusion:true`
		},
		policyMinDate() {
			return dayjs()
				.add(-1, "day")
				.toDate()
		},
		policyMaxDate() {
			return dayjs()
				.add(30, "day")
				.toDate()
		}
	},
	watch: {
		monthly() {
			this.form.paymentFrequency = this.monthly ? "monthly" : "annually"
			this.getPrice()
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.$validator.resume()
		})
		this.monthly = this.monthly === null ? false : this.monthly
		if (!this.form.people.length) {
			this.form.people.push(
				this.initPerson({
					index: 0,
					isInsured: true,
					isHolder: true,
					gender: "female"
				})
			)
			this.form.people.push(
				this.initPerson({
					index: 1
				})
			)
		}
		this.form.policyEffectiveDate = dayjs().format("YYYY-MM-DD")
	},
	validations() {
		return {}
	},
	methods: {
		toggleInsuredIsHolder(insuredIsHolder) {
			this.form.people[1].isHolder = !insuredIsHolder
		},
		toggleData(data) {
			this[data] = !this[data]
		},
		initPerson(data) {
			const p = {
				type: data.type,
				nameFormat: null,
				hkid: null,
				dateOfBirth: null,
				heightInCm: null,
				weightInKg: null,
				occupation: null,
				email: null,
				telephone: null,
				index: data.index,
				isHolder: data.isHolder || false,
				isOtherHolder: data.isOtherHolder || false,
				isInsured: data.isInsured || false
			}

			if (data.isInsured) {
				p.gender = data.gender || "female"
				p.placeOfResidence = "hong_kong"
				p.relationshipsToHolder = null
			}

			if (data.isHolder || data.isOtherHolder) {
				p.salutation = null
			}

			return p
		},
		addOtherHolder() {
			const index = this.form.people.length
			this.form.people.push(
				this.initPerson({
					index,
					isOtherHolder: true
				})
			)
		},
		async changeProduct() {
			if (
				this.productId &&
				this.productId.plan &&
				this.annualOnly.includes(this.productId.plan)
			) {
				this.monthly = false
			}
			this.form.appliedDiscounts = []
			await this.getPrice()
		},
		async applyDiscount(code) {
			this.discountCodeError = ""
			if (!code) return
			if (!this.productId) {
				this.discountCodeError = this.$t("asia.discount_code_error.no_product")
				return
			}
			if (
				this.form.appliedDiscounts
					.map(d => {
						return d.code.toLowerCase()
					})
					.includes(code.toLowerCase())
			) {
				this.discountCodeError = this.$t("asia.discount_code_error.applied")
				return
			}

			const newAppliedDiscounts = [code]
			this.isDiscountApplying = true
			await this.getPrice(newAppliedDiscounts)
			this.isDiscountApplying = false
			const appliedCodes = this.form.products[0].pricing.appliedDiscounts.map(
				d => d.code.toLowerCase()
			)
			if (!appliedCodes.includes(code.toLowerCase())) {
				this.discountCodeError = this.$t("asia.discount_code_error.invalid")
			}
			this.form.appliedDiscounts = this.form.products[0].pricing.appliedDiscounts
		},
		async removeDiscount() {
			this.discountCodeError = ""
			this.form.appliedDiscounts = []
			await this.getPrice()
		},
		async applyReferral(code) {
			this.referralCodeError = ""
			if (!code) return
			if (
				this.form.appliedReferrals.filter(r => {
					return r.internalCode === code
				}).length
			) {
				this.referralCodeError = this.$t("asia.referral_code_error.applied")
				return
			}

			this.isReferenceApplying = true
			this.form.appliedReferrals = []
			const res = await this.internals(code)
			if (res.errors || !res.length) {
				this.referralCodeError = this.$t("asia.referral_code_error.invalid")
			} else {
				this.form.appliedReferrals.push(res[0])
			}
			this.isReferenceApplying = false
		},
		async removeReferrals() {
			this.referralCodeError = ""
			this.form.appliedReferrals = []
			await this.getPrice()
		},
		removeHolder(index) {
			this.form.people.splice(index, 1)
			let i = 0
			this.form.people.forEach(p => {
				p.index = i++
			})
		},
		insuredAge() {
			if (!this.form.people[0].dateOfBirth) return null
			const today = dayjs()
			const birthday = dayjs(this.form.people[0].dateOfBirth)
			const age = today.diff(birthday, "year")
			return age
		},
		async onSubmit() {
			// If price is loading, return
			if (this.isLoading || this.isPriceLoading) return
			// Validate
			const isValid = await this.$validator.validate()
			if (!isValid) {
				this.$store.dispatch("showError", [
					"There are some missing or incorrect fields in the form"
				])
				// scroll to first error
				this.$scrollTo(`#${this.$validator.errors.items[0].field}`, 300, {
					offset: -100
				})
				return
			}

			const _this = this
			_this.isLoading = true
			_this.computeData()
			_this.insureds = _this.form.people.filter(p => {
				return p.isInsured
			})
			_this.holders = _this.form.people.filter(p => {
				return p.isHolder && !p.isInsured
			})
			try {
				_this.insured = await _this.createIndividual(_this.insureds[0])
				_this.holder = _this.holders.length
					? await _this.createIndividual(_this.holders[0])
					: _this.insured
				_this.link = _this.insureds[0].relationshipsToHolder
					? _this.insureds[0].relationshipsToHolder
					: "self"
				await _this.addLink(_this.link, _this.insured.id, _this.holder.id)
				await _this.createOtherHolders()
				_this.case = await _this.createCase(_this.insured.id, _this.holder.id)
				_this.addCaseFacts(_this.case.id)
				_this.proposal = await _this.addProposal(_this.case.id)
				await _this.addOfferToProposal(
					_this.case.id,
					_this.proposal.id,
					_this.productId
				)
				_this.$router.push({
					name: "CaseManagementDetail",
					params: {
						id: _this.case.id
					},
					query: {
						genericViewId: "overview",
						genericViewChildrenViewId: "proposalsList"
					}
				})
			} catch (errors) {
				console.log(errors)
			}
			_this.isLoading = false
		},
		async getPrice(newAppliedDiscounts) {
			this.isPriceLoading = true
			this.form.products = await this.getProducts(newAppliedDiscounts)
			this.isPriceLoading = false
		},
		getProducts(newAppliedDiscounts) {
			return new Promise(async (resolve, reject) => {
				const query = `query products_2($where: productWhereInput, $values: [keyValueInput], $discountCodes: [String]) {
          products_2(where: $where, values: $values){
            list {
              name
              pricing(values: $values, discountCodes: $discountCodes) {
                amount
                formattedPrice
                formattedOriginalPrice
                appliedDiscounts {
                  code
									formattedFlat
									formattedOriginalPrice
									ratio
                }
                appliedTaxes {
                  code
									formattedFlat
									formattedOriginalPrice
									ratio
                }
              }
              productId {
                plan
                type
                version
              }
							benefitGraph {
                typeId
                value
                #TODO: 'name' should be removed name after refactoring of sinle product layout done
                # Single product layout component should accept prop benefitInfo so that it can compose the tree with naming
                name
                description
                detailedValue
                isOptional
                options {
                  name
                  description
                  optionKey
                  value
                  typeId

                  children {
                    typeId
                    value
                    optionKey
                    name
                    description
                  }

                }
                children {
                  typeId
                  value
                  detailedValue
                  isOptional
                  options{
                    name
                    description
                    optionKey
                    value
                    typeId
                  }
                  #TODO: 'name' should be removed name after refactoring of sinle product layout done
                  # Single product layout component should accept prop benefitInfo so that it can compose the tree with naming
                  name
                  description
                  children {
                    typeId
                    value
                    detailedValue
                    isOptional
                    options{
                      name
                      description
                      optionKey
                      value
                      typeId
                    }
                    #TODO: 'name' should be removed name after refactoring of sinle product layout done
                    # Single product layout component should accept prop benefitInfo so that it can compose the tree with naming
                    name
                    description
                  }
                }
              }
            }
          }
        }`

				const variables = {
					discountCodes:
						newAppliedDiscounts || this.form.appliedDiscounts.map(d => d.code),
					where: {
						and: [
							{
								productId: this.productId
							}
						]
					},
					values: [
						{
							key: "paymentFrequency",
							value: {
								stringValue: this.form.paymentFrequency
							}
						},
						{
							key: "currencyCode",
							value: {
								stringValue: "HKD"
							}
						},
						{
							key: "insureds",
							value: {
								arrayValue: [
									{
										objectValue: [
											{
												key: "age",
												value: {
													numberValue: Number(this.insuredAge())
												}
											},
											{
												key: "isSmoking",
												value: {
													booleanValue: false
												}
											},
											{
												key: "gender",
												value: {
													stringValue: this.form.people[0].gender
												}
											}
										]
									}
								]
							}
						}
					]
				}
				await this.APIRequest("products_2", query, variables, resolve, reject)
			})
		},
		computePerson(person) {
			return {
				...person
			}
		},
		computeData() {
			const insured =
				this.form.people &&
				this.form.people.filter(p => {
					return p.isInsured
				})[0]
			const holder =
				this.form.people &&
				this.form.people.filter(p => {
					return p.isHolder
				})[0]
			const otherHolders =
				this.form.people &&
				this.form.people.filter(p => {
					return p.isOtherHolder
				})
			this.computedData = {
				insured: this.computePerson(insured),
				holder: this.computePerson(holder),
				otherHolders: otherHolders.map(h => {
					return this.computePerson(h)
				})
			}
		},
		computeFacts() {
			const facts = []
			this.form.illnessFacts.forEach(f => {
				const fact = {
					type: f.type,
					value: {
						booleanValue: f.value
					}
				}

				facts.push(fact)
			})
			this.form.otherFacts.forEach(f => {
				const fact = {
					type: f.type,
					value: {
						booleanValue: f.value
					}
				}

				facts.push(fact)
			})

			const height = {
				type: "heightInCm",
				value: {
					numberValue: Number(this.computedData.insured.heightInCm)
				}
			}
			facts.push(height)

			const weight = {
				type: "weightInKg",
				value: {
					numberValue: Number(this.computedData.insured.weightInKg)
				}
			}
			facts.push(weight)

			const occupation = {
				type: "occupation",
				value: {
					stringValue: this.computedData.insured.occupation
				}
			}
			facts.push(occupation)

			return facts
		},
		async createOtherHolders() {
			const _this = this
			_this.otherHolderIds = []
			const promiseArr = []
			const otherHolders = _this.form.people.filter(p => {
				return p.isOtherHolder
			})
			otherHolders.forEach(h => {
				promiseArr.push(
					new Promise(async (resolve, reject) => {
						try {
							_this.otherHolder = await _this.createIndividual(h)
							_this.otherHolderIds.push(_this.otherHolder.id)
							resolve()
						} catch (error) {
							reject(error)
						}
					})
				)
			})
			await Promise.all(promiseArr)
				.then()
				.catch(e => {
					return Promise.reject(e)
				})
		},
		checkIfNeedsManualUnderwriting() {
			const bmi =
				parseInt(this.computedData.insured.weightInKg) /
				((parseInt(this.computedData.insured.heightInCm) / 100) *
					(parseInt(this.computedData.insured.heightInCm) / 100))
			return (
				this.insuredAge() < 13 ||
				this.insuredAge() > 80 ||
				bmi < 17 ||
				bmi > 33 ||
				this.computedFacts
					.map(f => {
						return f.value.booleanValue
					})
					.includes(true) ||
				this.computedData.insured.occupation !== "001"
			)
		},
		createIndividual(person) {
			return new Promise(async (resolve, reject) => {
				const mutation = `mutation createIndividual($createIndividualInput: createIndividualInput!) {
          createIndividual(createIndividualInput: $createIndividualInput) {
            status
            createdStatus {
              id
            }
            errors
          }
        }`
				const createIndividualInput = {
					salutation: person.salutation,
					nameFormat: person.nameFormat,
					dateOfBirth: dayjs(person.dateOfBirth).format("YYYY-MM-DD"),
					gender: person.gender,
					addresses: [
						{
							type: "correspondenceAddress",
							fields: [
								{
									type: "correspondenceAddress",
									value: person.correspondenceAddress
								}
							]
						}
					],
					identities: [
						{
							type: "hkid",
							value: person.hkid
						}
					],
					contacts: [
						{
							type: "email",
							value: person.email
						},
						{
							type: "telephoneNumber",
							value: person.telephone
						}
					]
				}
				createIndividualInput.facts = []
				if (person.isInsured) {
					createIndividualInput.addresses.push({
						type: "placeOfResidence",
						fields: [
							{
								type: "address",
								value: person.placeOfResidence
							}
						]
					})
					createIndividualInput.facts = this.computeFacts()
				}

				if (person.isHolder) {
					createIndividualInput.type = "CUSTOMER"
					const applicantAcknowledgesDeclaration = {
						type: "applicantAcknowledgesDeclaration",
						value: {
							booleanValue: true
						}
					}
					createIndividualInput.facts.push(applicantAcknowledgesDeclaration)

					const applicantAcknowledgesCommissionDisclosure = {
						type: "applicantAcknowledgesCommissionDisclosure",
						value: {
							booleanValue: true
						}
					}
					createIndividualInput.facts.push(
						applicantAcknowledgesCommissionDisclosure
					)

					const applicantUnderstandsCancellationRights = {
						type: "applicantUnderstandsCancellationRights",
						value: {
							booleanValue: true
						}
					}
					createIndividualInput.facts.push(
						applicantUnderstandsCancellationRights
					)
				}

				await this.APIRequest(
					"createIndividual",
					mutation,
					{ createIndividualInput },
					resolve,
					reject
				)
			})
		},
		createCase(insuredId, holderId) {
			return new Promise(async (resolve, reject) => {
				const query = `mutation createCase($input: createCaseInput!) {
          createCase (input:$input) {
            createdStatus {
              id
            }
            status
            errors
          }
        }`

				const variables = {
					input: {
						holderId,
						insuredIds: [insuredId],
						otherHolderIds: this.otherHolderIds
					}
				}
				await this.APIRequest("createCase", query, variables, resolve, reject)
			})
		},
		addLink(link, sourceId, targetId) {
			return new Promise(async (resolve, reject) => {
				const query = `
          mutation addLink($linkInput: createLinkInput!){
            addLink(linkInput: $linkInput) {
              status
              errors
            }
          }
        `

				const variables = {
					linkInput: {
						link,
						sourceId,
						targetId
					}
				}

				await this.APIRequest("addLink", query, variables, resolve, reject)
			})
		},
		async addCaseFacts(caseId) {
			const promiseArr = []
			const _this = this
			promiseArr.push(
				new Promise(async () => {
					await _this.addFactToCase(caseId, {
						type: "settleMode",
						value: {
							stringValue: _this.form.settleMode
						}
					})
				})
			)

			if (_this.form.settleMode === "autopay") {
				promiseArr.push(
					new Promise(async () => {
						await _this.addFactToCase(caseId, {
							type: "bankName",
							value: {
								stringValue: _this.form.bankName
							}
						})
					})
				)
				promiseArr.push(
					new Promise(async () => {
						await _this.addFactToCase(caseId, {
							type: "bankAccountNumber",
							value: {
								stringValue: _this.form.bankAccountNumber
							}
						})
					})
				)
				promiseArr.push(
					new Promise(async () => {
						await _this.addFactToCase(caseId, {
							type: "bankAccountFullName",
							value: {
								stringValue: _this.form.bankAccountFullName
							}
						})
					})
				)
			}

			await Promise.all(promiseArr)
				.then()
				.catch(e => {
					return Promise.reject(e)
				})
		},
		addFactToCase(caseId, input) {
			return new Promise(async (resolve, reject) => {
				const query = `
          mutation addFactToCase($caseId: String!, $input: addFactInput!) {
            addFactToCase(caseId: $caseId, input: $input) {
              createdStatus {
                id
              }
              errors
              status
            }
          }
        `

				const variables = {
					caseId,
					input
				}

				await this.APIRequest(
					"addFactToCase",
					query,
					variables,
					resolve,
					reject
				)
			})
		},
		addProposal(caseId) {
			return new Promise(async (resolve, reject) => {
				const query = `
          mutation addProposal($caseId: String!, $addProposalInput: addProposalInput!) {
            addProposal(caseId: $caseId, input: $addProposalInput) {
              createdStatus {
                id
              }
              status
              errors
            }
          }
        `
				const variables = {
					caseId,
					addProposalInput: {
						referralCode:
							(this.form.appliedReferrals[0] &&
								this.form.appliedReferrals[0].internalCode) ||
							""
					}
				}

				await this.APIRequest("addProposal", query, variables, resolve, reject)
			})
		},
		addOfferToProposal(caseId, proposalId, productId) {
			return new Promise(async (resolve, reject) => {
				const query = `mutation addOfferToProposal(
          $caseId: String!
          $proposalId: String!
          $addOfferInput: addOfferInput!
        ) {
          addOfferToProposal(caseId: $caseId, proposalId: $proposalId, input: $addOfferInput) {
            createdStatus {
              id
            }
            status
            errors
          }
				}`

				const variables = {
					caseId,
					proposalId,
					addOfferInput: {
						productId,
						values: [
							{
								key: "paymentFrequency",
								value: {
									stringValue: this.form.paymentFrequency
								}
							},
							{
								key: "currencyCode",
								value: {
									stringValue: "HKD"
								}
							},
							{
								key: "insureds",
								value: {
									arrayValue: [
										{
											objectValue: [
												{
													key: "age",
													value: {
														numberValue: Number(this.insuredAge())
													}
												},
												{
													key: "isSmoking",
													value: {
														booleanValue: false
													}
												},
												{
													key: "gender",
													value: {
														stringValue: this.form.people[0].gender || "female"
													}
												}
											]
										}
									]
								}
							}
						],
						startDate: dayjs(this.form.policyEffectiveDate).format(
							"YYYY-MM-DD"
						),
						endDate: dayjs(this.form.policyEffectiveDate)
							.add(1, "year")
							.add(-1, "day")
							.format("YYYY-MM-DD"),
						premium: {
							discountCodes: this.form.appliedDiscounts.map(d => d.code)
						}
					}
				}

				await this.APIRequest(
					"addOfferToProposal",
					query,
					variables,
					resolve,
					reject
				)
			})
		},
		internals(code) {
			return new Promise(async (resolve, reject) => {
				const query = `query internals($internalWhereInput: internalWhereInput) {
          internals(where: $internalWhereInput) {
            totalCount 
            list {
              name
              internalCode
            }
          }
        }`

				const variables = {
					internalWhereInput: {
						internalCode: code
					}
				}
				await this.APIRequest("internals", query, variables, resolve, reject)
			})
		},
		async APIRequest(type, query, variables, resolve, reject) {
			try {
				const { data } = await api({
					query,
					variables
				})

				if (data.errors) {
					reject(data.errors)
				} else {
					resolve(data[type].createdStatus || data[type].list || data[type])
				}
			} catch (error) {
				reject(error)
			}
		}
	},
	created() {
		this.$validator.pause()
	}
}
</script>
<style scoped>
@import "../../../../assets/css/tokens.css";
.card-header {
	border-top-left-radius: 4px;
	border-top-right-radius: 4px;
}
.asia-pricing-box-sticky {
	position: initial;
}
@media (--md) {
	.asia-pricing-box-sticky {
		position: sticky;
		top: 24px;
	}
}
</style>
